import { NextPage } from 'next';

import { ImageLoaderProps } from 'next/image';
import { KnotEntry } from '../../api/contentful/getKnot';
import { InternalLink } from '../../components/Clickables/InternalLink/InternalLink';
import { Container } from '../../components/Container/Container';
import { Grid } from '../../components/Grid/Grid';
import { Heading } from '../../components/Heading/Heading';
import { MetaTags } from '../../components/MetaTags/MetaTags';
import { NextImageWrapper } from '../../components/NextImage/NextImageWrapper';
import { Text } from '../../components/Text/Text';
import { ContentfulResizeTypes, getContentfulLoader } from '../../helpers/imageLoaders/contentful';

export interface IKnotsProps {
  knots: KnotEntry[];
}

export const Knots: NextPage<IKnotsProps> = ({ knots }) => {
  const featuredImage = knots.length > 0 ? knots[0].fields.featuredImage?.fields.file : undefined;
  return (
    <Container>
      <MetaTags
        title="Tying Fishing Knots"
        description="Learn how to tie the best knots for fishing with these easy how-to tutorials."
        link="/knots"
        image={featuredImage?.url}
      />

      <div className="my-16 flex flex-col items-center">
        <Heading as="h1" className="mb-4 text-center">
          Tying Fishing Knots
        </Heading>
        <Text className="text-center">
          You don&apos;t have to be an avid angler to be the best angler. One of the best kept
          secrets to catching a big fish is the ability to tie a good knot. Learn how to tie the
          best knots for fishing with these easy how-to tutorials.
        </Text>
      </div>
      <Grid className="mx-auto mb-12" style={{ maxWidth: 920 }}>
        {knots.map(knot => {
          const image = knot.fields.featuredImage?.fields.file;
          const imageWidth = image?.details.image?.width || 200;
          const imageHeight = image?.details.image?.height;
          const contentfulLoader = ({ src, width, quality }: ImageLoaderProps): string =>
            getContentfulLoader(src, width, imageWidth, imageHeight, {
              quality,
              resize: ContentfulResizeTypes.FILL,
            });

          return (
            <InternalLink
              to={`/knots/${knot.fields.slug}`}
              key={knot.fields.slug}
              className="col-span-12 sm:col-span-6"
            >
              <div className="relative flex">
                {image && imageWidth && imageHeight && (
                  <NextImageWrapper
                    loader={contentfulLoader}
                    width={imageWidth}
                    height={imageHeight}
                    src={image.url}
                    alt={knot.fields.name}
                  />
                )}
                <div
                  className="absolute bottom-0 w-full p-2"
                  style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                >
                  <Heading as="h2" className="mb-0 text-sea-foam" variant="xs">
                    {knot.fields.name}
                  </Heading>
                  <Text className="mb-0 text-sea-foam">{knot.fields.shortDescription}</Text>
                </div>
              </div>
            </InternalLink>
          );
        })}
      </Grid>
    </Container>
  );
};
